import { FC, JSX, useContext, useEffect, useRef, useState } from "react";
import CreateProject from "@app/projects/create/createProject";
import CreateProjectService from "@app/projects/create/createProjectService";
import { Fields, RegionSetParseResults } from "@app/projects/create/interfaces";
import getOptions from "@app/projects/create/data/options";
import GeographyService from "@app/geography/geographyService";
import WizardStep from "@app/projects/create/WizardStep";
import ZipProjectService from "@app/projects/zipProjectService";
import { convertCreateProjectToZipProjectRequestModel } from "@app/projects/zip-projects/interfaces/createZipProjectRequestModel";
import { ResourceTagModel } from "@app/tags/import/types";
import { getGenericErrorMessage } from "@app/shared/utility/getGenericErrorMessage";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import ListAccordion from "@app/shared/ListAccordion";
import PageSection from "@app/shared/PageSection";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserInfo, UserContext } from "@app/user/UserContext";

interface CreateProjectStepReviewProps {
  completionTracker: boolean[];
  createProjectModel: CreateProject;
  baseUrl: string;
  progressList: string[];
  removeNonAlternateMemberships: () => void;
  currentIndex: number;
  hideStrictTimeDistance: boolean;
  isDataShareEligible: boolean;
  isZipProject: boolean;
  hideMotiveAppropriatenessScore: boolean;
  hideCMSApplicationCredit: boolean;
  hideClosestProviderReportOption: boolean;
  hideEnterpriseComplianceDashboard: boolean;
  hidePracAtLocCondifence: boolean;
  hideProviderClaimsActivity: boolean;
}

const CreateProjectStepReview: FC<CreateProjectStepReviewProps> = (props: CreateProjectStepReviewProps) => {
  const createProjectService = useRef(new CreateProjectService()).current;
  const zipProjectService = useRef(new ZipProjectService()).current;
  const geographyService = useRef(new GeographyService()).current;
  const filteredTags = useRef<ResourceTagModel[]>([]);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);

  const [region, setRegion] = useState<RegionSetParseResults>(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (props.createProjectModel.serviceAreaRegionSetId) {
      // changing this from await so the component doesn't hang while this is being processed
      geographyService.getParsedRegionSet(props.createProjectModel.serviceAreaRegionSetId).then((data) => {
        setRegion(data);
      });
    }

    filteredTags.current = props.createProjectModel.tags?.filter(tag => tag.tagGroupName.length > 0 && tag.tagName.length > 0);
  }, []);

  const handleFormSubmit = async (runNow: boolean): Promise<void> => {
    try {
      setSubmitted(true);

      props.removeNonAlternateMemberships();
      const project: CreateProject = structuredClone(props.createProjectModel);
      if (!project.marketProviderSetId && project.useMarketProvidersForImpact) {
        project.useMarketProvidersForImpact = false;
      }

      if (!props.isZipProject) {
        await createProjectService.createProject(project, runNow);
      } else {
        await zipProjectService.createZipProject(convertCreateProjectToZipProjectRequestModel(project, runNow));
      }

      window.location.href = window.customUIBaseUrl + "/";
    } catch (newError) {
      setError(newError);
      setSubmitted(false);
    }
  }

  const internalGetOptions = (user: UserInfo) =>
    getOptions<CreateProject>(
      {
        accuracyAndAdequacy: props.createProjectModel.accuracyAndAdequacy,
        affiliationsDisplayOnNetworkAccordion: props.createProjectModel.affiliationsDisplayOnNetworkAccordion,
        enableNetworkIntegrityOption: props.createProjectModel.adequacyTemplateCollectionId != null,
        enablePatternOfCareProvidersOption: props.createProjectModel.hasZipSummaries && props.createProjectModel.showClosestProviderReport,
        enablePgrConfidenceTierOption: props.createProjectModel.adequacyTemplateCollectionId != null,
        enableRegulatoryExceptionReportsOption: props.createProjectModel.hasZipSummaries && props.createProjectModel.showClosestProviderReport,
        hideClosestProviderReport: props.hideClosestProviderReportOption,
        hideCMSApplicationCredit: props.hideCMSApplicationCredit,
        hideEnterpriseComplianceDashboard: props.hideEnterpriseComplianceDashboard,
        hideMotiveAppropriatenessScore: props.hideMotiveAppropriatenessScore,
        hidePciData: !props.createProjectModel.adequacyTemplateCollectionId,
        hidePracAtLocCondifence: props.hidePracAtLocCondifence,
        hideProviderClaimsActivity: props.hideProviderClaimsActivity,
        hideStrictTimeDistance: props.hideStrictTimeDistance,
        isDataShareEligible: props.isDataShareEligible,
        isZipProject: props.isZipProject,
        showAffiliationsInsights: props.createProjectModel.showAffiliationsInsights,
        showNcpInStateProvidersOnly: enabledFeatureFlags.has(Features.ZIP_NEXT_CLOSEST_PROVIDERS) && props.createProjectModel.showNextClosestNetworkProviders,
        showNetworkIntegrityDashboardChecked: props.createProjectModel.showNetworkIntegrityDashboard,
        showPciDataChecked: props.createProjectModel.showPciData,
        showZipAnalysisNextClosestProviders: enabledFeatureFlags.has(Features.ZIP_NEXT_CLOSEST_PROVIDERS),
        showZipGapAgeTracking: enabledFeatureFlags.has(Features.ZIP_GAP_AGE_TRACKING),
        useAccuracy: props.createProjectModel.accuracyTemplateCollectionId !== null,
        userRoles: user.roles
      }
    )
      .reduce((acc, current) => acc = [...acc, ...current.fields], new Array<Fields<CreateProject>>())
      .filter((option) => props.createProjectModel[option.key as keyof CreateProject]);

  const handleIsNextDisabled = () => {
    return !(
      props.completionTracker.filter((_, index) => (props.currentIndex - 1) > index).every(step => step) &&
      !submitted
    );
  }

  const getReadableServiceAreaResults = () => {
    const readableResults: Array<JSX.Element[] | JSX.Element> = [];
    const results = region;

    readableResults.push(results.cbsas.map((cbsaName: string) => (<ListAccordion key={cbsaName} titleElement={<span>{cbsaName} (CBSA)</span>} />)));

    readableResults.push(results.states.map((state) => (
      <ListAccordion key={state.name} titleElement={<span>{state.name}</span>}>
        {state.counties.map((county) => (
          <ListAccordion key={county.name} titleElement={<span>{`${county.name} (${props.createProjectModel.countyStatuses.find((item) => item.countySSA === county.countySSA).status})`}</span>}>
            {county.countyZips.map((countyZip: string) => (
              <li key={countyZip}>{countyZip}</li>
            ))}
          </ListAccordion>
        ))}
        {state.zips.map((zip) => (
          <li key={zip}>{zip}</li>
        ))}
      </ListAccordion>
    )));

    return (<ul>{readableResults}</ul>);
  }

  const getReadableNetworkTaxonomyIdentifierResults = () => {
    return (
      <ul>
        <ListAccordion titleElement={<span>Identifiers</span>}>
          {props.createProjectModel.networkTaxonomy.identifiers.map((identifier) => (
            <ListAccordion key={identifier.name} titleElement={<span>{`${identifier.name}: ${identifier.value}`}</span>} />
          ))}
        </ListAccordion>
      </ul>
    )
  }

  return (
    <>
      {error && (
        <PageSection>
          <div className="row">
            <div className="col-12">
              <QESAlert type="danger" classNames="m10" persist={true}>
                {getGenericErrorMessage()}
              </QESAlert>
            </div>
          </div>
        </PageSection>
      )}
      <WizardStep
        {...props}
        heading="Create a New Project"
        subheading="Confirm / Review"
        baseUrl={props.baseUrl}
        prevUrl={(props.currentIndex - 1).toString()}
        progressList={props.progressList}
        onSubmitClick={(e) => handleFormSubmit(e)}
        isPrevDisabled={submitted}
        isNextDisabled={handleIsNextDisabled()}
        submitted={submitted}
      >
        <UserContext.Consumer>
          {(user) => (
            <>
              <div className="form-group row">
                <label className="col-3 demibold">Project Name: </label>
                <div className="col-4">{props.createProjectModel.name}</div>
              </div>
              <div className="form-group row">
                <label className="col-3 demibold">Description: </label>
                <div className="col-4">{props.createProjectModel.description}</div>
              </div>
              <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
                <div className="form-group row">
                  <label className="col-3 demibold">Groups/Tags: </label>
                  <div className="col-4">
                    {filteredTags.current.length > 0 && filteredTags.current.map((tag, index) =>
                      <div key={`tagcontainer${index}`}>
                        <label className="me-2 demibold">Group: </label>{tag.tagGroupName}
                        <label className="ms-3 me-2 demibold">Tag Name: </label>{tag.tagName}
                      </div>
                    )}
                  </div>
                </div>
              </RoleRestricted>
              <RoleRestricted allowedRoles={[Roles.RESOURCE_ADMIN]}>
                <div className="form-group row">
                  {props.createProjectModel.isPrivate && (
                    <div className="offset-3 col-4">This is a Private Project (can be administered only by you)</div>
                  )}
                  {!props.createProjectModel.isPrivate && (
                    <div className="offset-3 col-4">This is a non-Private Project (can be administered by you and other users with permission to adminster non-Private projects)</div>
                  )}
                </div>
              </RoleRestricted>
              {!props.isZipProject && (
                <>
                  <div className="form-group row">
                    <label className="col-3 demibold">Accuracy Template Collection: </label>
                    <div className="col-4">{props.createProjectModel.accuracyTemplateCollectionDescription}</div>
                  </div>
                  <div className="form-group row">
                    <label className="col-3 demibold">Accuracy Template: </label>
                    <div className="col-4">{props.createProjectModel.accuracyTemplateCorrelationId}</div>
                  </div>
                  <div className="form-group row">
                    <label className="col-3 demibold">Accuracy Specialty to BDX UID Code Map: </label>
                    <div className="col-4">{props.createProjectModel.accuracySpecialtyToQdpCodeMapSetDescription}</div>
                  </div>
                  <div className="form-group row">
                    <label className="col-3 demibold">Inspected Insurances: </label>
                    <ul className="col-4 list-group-flush">
                      {props.createProjectModel.inspectedInsurancesList.map((ins) => (
                        <li key={ins.uid} className="list-group-item">{ins.planName}: {ins.uid}</li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
              <div className="form-group row">
                <label className="col-3 demibold">Adequacy Template Collection: </label>
                <div className="col-4">{props.createProjectModel.adequacyTemplateCollectionDescription}</div>
              </div>
              <div className="form-group row">
                <label className="col-3 demibold">Adequacy Template: </label>
                <div className="col-4">{props.createProjectModel.adequacyTemplateCorrelationId}</div>
              </div>
              {props.createProjectModel.memberPopDescription !== "" && (
                <div className="form-group row">
                  <label className="col-3 demibold">Member Population: </label>
                  <div className="col-4">{props.createProjectModel.memberPopDescription}
                    {props.createProjectModel.memberSetAlias !== "" && (
                      <label className="ms-1">
                        (Alias: {props.createProjectModel.memberSetAlias})
                      </label>)}
                  </div>
                </div>
              )}
              {props.createProjectModel.memberPopDescription2 !== "" && (
                <div className="form-group row">
                  <label className="col-3 demibold">Second Member Population: </label>
                  <div className="col-4">{props.createProjectModel.memberPopDescription2}
                    {props.createProjectModel.memberSetAlias2 !== "" && (
                      <label className="ms-1">
                        (Alias: {props.createProjectModel.memberSetAlias2})
                      </label>)}
                  </div>
                </div>
              )}
              <RoleRestricted allowedRoles={[Roles.MEMBERSHIP_RISK_USER]}>
                {props.createProjectModel.membershipRiskMemberSetAlias !== "" && (
                  <div className="form-group row">
                    <label className="col-3 demibold">Risk Analysis Member Population: </label>
                    <div className="col-4">
                      {props.createProjectModel.membershipRiskMemberSetAlias}
                    </div>
                  </div>
                )}
              </RoleRestricted>
              <div className="form-group row">
                <label className="col-3 demibold">Network Providers: </label>
                <div className="col-4">{props.createProjectModel.networkProviderDescription}</div>
              </div>
              {props.createProjectModel.prospectProviderDescription !== "" && (
                <div className="form-group row">
                  <label className="col-3 demibold">Prospect Providers: </label>
                  <div className="col-4">{props.createProjectModel.prospectProviderDescription}</div>
                </div>
              )}
              {props.createProjectModel.marketProviderDescription !== "" && (
                <>
                  <div className="form-group row">
                    <label className="col-3 demibold">Market Providers: </label>
                    <div className="col-4">{props.createProjectModel.marketProviderDescription}</div>
                  </div>
                </>
              )}
              {(props.createProjectModel.networkTaxonomy) &&
                <>
                  {props.createProjectModel.networkTaxonomy.fileType &&
                    <div className="form-group row">
                      <label className="col-3 demibold">File Type:</label>
                      <div className="col-4">{props.createProjectModel.networkTaxonomy.fileType}</div>
                    </div>
                  }
                  {props.createProjectModel.networkTaxonomy.fileTypeReason &&
                    <div className="form-group row">
                      <label className="col-3 demibold">Other File Type:</label>
                      <div className="col-4">{props.createProjectModel.networkTaxonomy.fileTypeReason}</div>
                    </div>
                  }
                  {props.createProjectModel.networkTaxonomy.projectUtilizationCategory &&
                    <div className="form-group row">
                      <label className="col-3 demibold">Project Utilization Category:</label>
                      <div className="col-4">{props.createProjectModel.networkTaxonomy.projectUtilizationCategory}</div>
                    </div>
                  }
                  {props.createProjectModel.networkTaxonomy.lineOfBusiness &&
                    <div className="form-group row">
                      <label className="col-3 demibold">Line Of Business:</label>
                      <div className="col-4">{props.createProjectModel.networkTaxonomy.lineOfBusiness}</div>
                    </div>
                  }
                  {props.createProjectModel.networkTaxonomy.lineOfBusinessTypeId &&
                    <div className="form-group row">
                      <label className="col-3 demibold">Line Of Business Type:</label>
                      <div className="col-4">{props.createProjectModel.networkTaxonomy.lineOfBusinessType}</div>
                    </div>
                  }
                  {props.createProjectModel.networkTaxonomy.identifiers && props.createProjectModel.networkTaxonomy.identifiers.length > 0 &&
                    <div className="form-group row">
                      <label className="col-3 demibold">Identifiers:</label>
                      <div className="col-8">
                        {getReadableNetworkTaxonomyIdentifierResults()}
                      </div>
                    </div>
                  }
                </>
              }
              <div className="form-group row">
                <label className="col-3 demibold">Service Area: </label>
                {!region ? <div className="col-4">Loading...</div> : (
                  <div className="col-8">
                    {getReadableServiceAreaResults()}
                  </div>
                )}
              </div>
              {props.createProjectModel.accessCodeMapSetId !== null &&
                <div className="form-group row">
                  <label className="col-3 demibold">Access Region to Classification Code Map: </label>
                  <div className="col-4">{props.createProjectModel.accessCodeMapSetDescription}</div>
                </div>
              }
              {internalGetOptions(user).length > 0 ? (
                <div className="form-group row">
                  <label className="col-3 demibold mb0">Project Options: </label>
                  {internalGetOptions(user).map((option, index) => <div key={option.title} className={`col-9 ${index > 0 ? "offset-3" : ""}`}>{option.title}</div>)}
                </div>
              ) : null}
            </>
          )}
        </UserContext.Consumer>
      </WizardStep>
    </>
  );
}

export default CreateProjectStepReview;
